import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import { useSpace } from '../../../hooks/useSpace';
import { useAuth } from '../../../hooks/useAuth';
import { getNextPageIndex, pageRange } from '../../../utils/pagination';
import { keys } from './keys';
import { EnrichedNotification } from '../types/notification';

const PAGE_SIZE = 20;

async function fetchNotifications(spaceId: string, userId: string, pageIndex: number): Promise<EnrichedNotification[]> {
  const { from, to } = pageRange(pageIndex, PAGE_SIZE);
  const { data: notifications, error } = await apiClient
    .from('notifications')
    .select()
    .eq('space_id', spaceId)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .range(from, to);

  if (error) {
    throw error;
  }

  // entity_id is polymorphic (no FK to embed), so resolve reminder titles of
  // this page in a single follow-up query and merge them in.
  const reminderIds = [
    ...new Set(
      notifications
        .filter((notification) => notification.entity_type === 'reminder' && notification.entity_id)
        .map((notification) => notification.entity_id as string)
    ),
  ];

  const remindersById = new Map<
    string,
    { title: string; due_date: string; subject_id: string | null; provider_id: string | null }
  >();

  if (reminderIds.length > 0) {
    const { data: reminders, error: remindersError } = await apiClient
      .from('reminders')
      .select('id, title, due_date, subject_id, provider_id')
      .in('id', reminderIds);

    if (remindersError) {
      throw remindersError;
    }

    reminders.forEach((reminder) => {
      remindersById.set(reminder.id, {
        title: reminder.title,
        due_date: reminder.due_date,
        subject_id: reminder.subject_id,
        provider_id: reminder.provider_id,
      });
    });
  }

  // Same polymorphic resolution for event notifications.
  const eventIds = [
    ...new Set(
      notifications
        .filter((notification) => notification.entity_type === 'event' && notification.entity_id)
        .map((notification) => notification.entity_id as string)
    ),
  ];

  const eventsById = new Map<string, { title: string; subject_id: string | null }>();

  if (eventIds.length > 0) {
    const { data: events, error: eventsError } = await apiClient
      .from('events')
      .select('id, title, subject_id')
      .in('id', eventIds);

    if (eventsError) {
      throw eventsError;
    }

    events.forEach((event) => {
      eventsById.set(event.id, { title: event.title, subject_id: event.subject_id });
    });
  }

  return notifications.map((notification) => ({
    ...notification,
    reminder:
      notification.entity_type === 'reminder' && notification.entity_id
        ? remindersById.get(notification.entity_id) ?? null
        : null,
    event:
      notification.entity_type === 'event' && notification.entity_id
        ? eventsById.get(notification.entity_id) ?? null
        : null,
  }));
}

type Options = {
  enabled?: boolean;
};

export default function useGetNotifications(options?: Options) {
  const { currentSpace } = useSpace();
  const { user } = useAuth();

  return useInfiniteQuery({
    queryKey: keys.list(currentSpace?.id ?? '', user?.id ?? ''),
    queryFn: ({ pageParam }) => fetchNotifications(currentSpace!.id, user!.id, pageParam),
    initialPageParam: 0,
    getNextPageParam: getNextPageIndex(PAGE_SIZE),
    enabled: (options?.enabled ?? true) && !!currentSpace?.id && !!user?.id,
  });
}
